'use client';
import { type StaticImageData } from 'next/image';

import { ImageWrapper } from '@module/mdrt-org/shared/components/image-wrapper/image-wrapper';
import { ResourceTypeIcon } from '@module/mdrt-org/shared/components/resource-type-icon/resource-type-icon';
import { Link } from '@module/mdrt-org/shared/i18n/i18n-navigation-link';
import { type ContentType } from '@module/mdrt-org/shared/utils/data/constants/content-type';
import {
  GTM_ACTION_CLICK,
  GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
  GTM_EVENT_USER_INTERACTIONS,
} from '@shared/utils/constants/gtm';
import { sendGtmEvent } from '@shared/utils/helpers/send-gtm-event';

import styles from './recommendation-section.module.scss';

const PARENT_CLASSNAME = 'recommendation-section';

export const RecommendationSection = ({
  recommendations,
  sectionTitle,
}: {
  recommendations: Array<{
    contentType: ContentType;
    isExternal: boolean;
    people: string[];
    thumbnail: {
      altText: string;
      fallbackSrc?: StaticImageData | string;
      url: string;
    };
    title: string;
    url: string;
  }>;
  sectionTitle: string;
}) => {
  return (
    <>
      <h3 className={styles[`${PARENT_CLASSNAME}__section-title`]}>{sectionTitle}</h3>
      {recommendations.length > 0 &&
        recommendations.map(({ contentType, isExternal, people, thumbnail, title, url }) => {
          return (
            <Link
              className={styles[`${PARENT_CLASSNAME}__content`]}
              href={url}
              key={`${title.toLowerCase().replaceAll(/\s+/gu, '-')}`}
              onClick={() => {
                sendGtmEvent({
                  action: GTM_ACTION_CLICK,
                  carousel_header: sectionTitle,
                  event: GTM_EVENT_USER_INTERACTIONS,
                  eventName: GTM_EVENT_NAME_INTERNAL_LINK_CLICK,
                  label: 'recommendation_section',
                });
              }}
              target={isExternal ? '_blank' : undefined}
            >
              <ImageWrapper
                alt={thumbnail.altText}
                className={styles[`${PARENT_CLASSNAME}__image`]}
                fallbackSrc={thumbnail.fallbackSrc}
                height={88}
                sizes="88px"
                src={thumbnail.url}
                width={88}
              />
              <div>
                <h4 className={styles[`${PARENT_CLASSNAME}__title`]}>{title}</h4>
                {people !== undefined && people.length > 0 && (
                  <div className={styles[`${PARENT_CLASSNAME}__details`]}>
                    <div className={styles[`${PARENT_CLASSNAME}__icon-container`]}>
                      <ResourceTypeIcon contentType={contentType} isExternal={isExternal} />
                    </div>
                    <div
                      className={styles[`${PARENT_CLASSNAME}__authors`]}
                      title={people.length >= 2 ? people.join(', ') : undefined}
                    >
                      <span>{people[0]}</span>
                      {people.length >= 2 && <span> +{people.length - 1}</span>}
                    </div>
                  </div>
                )}
              </div>
            </Link>
          );
        })}
    </>
  );
};
